import { toPx } from '@infinex/ui-utils/dom';

/**
 * Create a scroll mask for a given distance, orientation, and scrollbar width.
 *
 * @example
 * ```tsx
 * return <div style={{ mask: scrollMask(16), overflowY: 'scroll' }} />;
 * ```
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/CSS/mask-image
 *
 * @param distance - The distance to mask. Can be a single number or a tuple for the start and end.
 * @param orientation - The orientation of the mask. Default is `"vertical"`.
 * @param scrollbar - The scrollbar offset. Default is `0`.
 *
 * @returns The CSS `mask-image` value.
 */
export function scrollMask(
  distance: Spacing | Edges,
  scrollbar: Spacing = 0,
  orientation: 'vertical' | 'horizontal' = 'vertical'
) {
  const isVertical = orientation === 'vertical';
  const scrollbarDirection = isVertical ? 'to left' : 'to top';
  const scrollbarMask = `linear-gradient(${scrollbarDirection}, #fff ${toPx(scrollbar)}, transparent ${toPx(scrollbar)})`;

  const [start, end] = toEdges(distance);
  const contentDirection = isVertical ? 'to bottom' : 'to right';
  const contentMask = `linear-gradient(${contentDirection}, transparent, #fff ${start}, #fff calc(100% - ${end}), transparent 100%)`;

  return `${scrollbarMask}, ${contentMask}`;
}

function toEdges(distance: Spacing | Edges) {
  return (Array.isArray(distance) ? distance : [distance, distance]).map(toPx);
}

type Spacing = string | number;
type Edges = [start: Spacing, end: Spacing];
